import React, { useContext, useState } from "react";
import { render as toText } from "datocms-structured-text-to-plain-text";

import { Headline } from "../../Headline";
import { ImageWithGrid } from "../../Image";
import { Tagline } from "../../Tagline/Tagline";
import { StructuredTextRenderer } from "../../StructuredTextRenderer";
import { TeaserButton } from "../TeaserButton/TeaserButton";

import ElementContext from "../../ElementContext";

import { useIsDesktop } from "../../../lib/hooks/mediaQueries";
import { useIsTablet } from "../../../lib/hooks/mediaQueries";
import { generateClassString as gcs } from "../../../lib/helper/generateClassString";

import Link from "../../Link";
import translate from "../../../lib/translate";
import GeneralPageContext from "../../../templates/GeneralPageContext";
import { CompareCheckBox } from "../../../page-components/camera/camera-comparison/CompareCheckBox/CompareCheckBox";

import * as styles from "./TeaserCard.module.less";

export function TeaserCard({
    isNew,
    teaserData,
    withoutGrid = false,
    children,
    cardClass,
    headingClass,
    imageClass,
    contentClass,
    hideButtonLabel,
    FixedContent = <></>,
    HoveredContent,
    // gridSize,
    hasLightBackground = true,
    fullWidthOnMobile = false,
    cameraId,
    noContent = false,
    withHover = false,
    customLinkData = {}
}) {
    const {
        layout: { showAnchorNavigation },
        cameraTeaserWithCompare = false
    } = useContext(GeneralPageContext);

    const isDesktop = useIsDesktop();

    const isTablet = useIsTablet();

    const [hovered, setHovered] = useState(false);

    const { background } = useContext(ElementContext);

    if (!teaserData) return null;

    const {
        type,
        image,
        tagline,
        headline,
        semanticOfHeadline,
        division,
        text,
        link,
        url,
        buttonLabel,
        document,
        gridSize
    } = teaserData;

    const targetBlank = type === "DatoCmsFair" ? { target: "_blank" } : {};
    const ariaButtonLabel = buttonLabel || headline;

    const hasStructuredTextLink =
        ["DatoCmsTeaser", "DatoCmsCameraAccessory"].includes(type) &&
        typeof link === "object" &&
        link?.value == null &&
        !url;

    const noLink = hasStructuredTextLink && !toText(link);

    return (
        <div
            className={gcs(
                styles.teaserCard,
                cardClass,
                [!image, styles.noSlideDown],
                [fullWidthOnMobile, styles.fullWidthOnMobile],
                [!withHover && noLink, styles.noHover]
            )}
            onMouseEnter={() => {
                if (!withHover && noLink) return;
                setHovered(true);
            }}
            onMouseLeave={() => {
                if (!withHover && noLink) return;
                setHovered(false);
            }}
        >
            {/* Compare Checkbox for Camera Filter */}
            {cameraTeaserWithCompare && cameraId && (
                <CompareCheckBox cameraId={cameraId} />
            )}

            {/* Teaser Image */}
            {image && (
                <div className={gcs(styles.teaserImage, styles.imageWrapper)}>
                    {cameraTeaserWithCompare && isNew && (
                        <span className={styles.isNewBadge}>
                            {translate("camera.item.new")}
                        </span>
                    )}

                    <ImageWithGrid
                        className={gcs(styles.teaserImage, imageClass, [
                            cameraId,
                            styles.cameraTeaserImage
                        ])}
                        gridSize={
                            gridSize ||
                            (showAnchorNavigation ? "s" : isDesktop ? "s" : "m")
                        }
                        gridDisable={withoutGrid || !hovered}
                        image={image}
                    />

                    {hasStructuredTextLink ? (
                        <StructuredTextRenderer
                            data={link}
                            itemLinkClass={styles.teaserCardLink}
                            itemLinkState={customLinkData}
                            noContainer
                            plainLink
                            emptyLink
                        />
                    ) : (
                        <Link
                            to={url}
                            linkClass={styles.teaserCardLink}
                            aria-label={ariaButtonLabel}
                            {...targetBlank}
                        />
                    )}
                </div>
            )}

            {/* Teaser Heading */}
            <div className={gcs(styles.teaserTopContent, headingClass)}>
                {tagline && <Tagline>{tagline}</Tagline>}

                {/* {division &&
                    (Array.isArray(division) ? (
                        division.map((div) => (
                            <Tagline
                                key={div.id}
                                hasLightBackground={hasLightBackground}
                            >
                                {div.name}
                            </Tagline>
                        ))
                    ) : (
                        <Tagline hasLightBackground={hasLightBackground}>
                            {division.name}
                        </Tagline>
                    ))} */}

                <Headline
                    headlineClass={styles.teaserContentHeadline}
                    semanticOfHeadline={semanticOfHeadline}
                >
                    {headline}
                </Headline>

                {hasStructuredTextLink ? (
                    <StructuredTextRenderer
                        data={link}
                        itemLinkClass={styles.teaserCardLink}
                        itemLinkState={customLinkData}
                        noContainer
                        plainLink
                        emptyLink
                    />
                ) : (
                    <Link
                        to={url}
                        linkClass={styles.teaserCardLink}
                        aria-label={ariaButtonLabel}
                        {...targetBlank}
                    />
                )}
            </div>

            <div
                className={gcs(
                    styles.teaserBottomContent,
                    "teaserBottomContent"
                )}
            >
                {!noContent && (
                    <div className={hovered ? styles.hovered : ""}>
                        <TeaserButton
                            structuredText={link}
                            url={url}
                            label={ariaButtonLabel}
                            document={document}
                            hideLabel={hideButtonLabel ?? !hovered}
                        />

                        {/* Teaser Content - space for custom content */}
                        <div
                            className={gcs(
                                styles.content,
                                [hovered, styles.contentHidden],
                                contentClass
                            )}
                        >
                            {children}

                            <p
                                className={
                                    !hasLightBackground ? styles.lightColor : ""
                                }
                            >
                                {typeof text === "object" ? (
                                    <StructuredTextRenderer
                                        data={text}
                                        // plainLink
                                        noContainer
                                        itemLinkType="secondary"
                                        contentElementType={"TeaserCard"}
                                    />
                                ) : (
                                    <span>{text}</span>
                                )}
                            </p>

                            {hasStructuredTextLink ? (
                                <StructuredTextRenderer
                                    data={link}
                                    itemLinkClass={styles.teaserCardLink}
                                    itemLinkState={customLinkData}
                                    noContainer
                                    plainLink
                                    emptyLink
                                />
                            ) : (
                                <Link
                                    to={url}
                                    linkClass={styles.teaserCardLink}
                                    aria-label={ariaButtonLabel}
                                    {...targetBlank}
                                />
                            )}
                        </div>

                        {HoveredContent && (
                            <div
                                className={gcs(
                                    styles.content,
                                    [!hovered, styles.contentHidden],
                                    contentClass
                                )}
                            >
                                {HoveredContent}
                                {hasStructuredTextLink ? (
                                    <StructuredTextRenderer
                                        data={link}
                                        itemLinkClass={styles.teaserCardLink}
                                        noContainer
                                        plainLink
                                        emptyLink
                                    />
                                ) : (
                                    <Link
                                        to={url}
                                        linkClass={styles.teaserCardLink}
                                        aria-label={ariaButtonLabel}
                                        {...targetBlank}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}

                <div className={gcs(styles.content, styles.endContent)}>
                    {!isTablet && (
                        <>
                            {hasStructuredTextLink ? (
                                <StructuredTextRenderer
                                    data={link}
                                    itemLinkClass={styles.teaserCardLink}
                                    itemLinkState={customLinkData}
                                    noContainer
                                    plainLink
                                    emptyLink
                                />
                            ) : (
                                <Link
                                    to={url}
                                    linkClass={styles.teaserCardLink}
                                    aria-label={ariaButtonLabel}
                                    {...targetBlank}
                                />
                            )}
                        </>
                    )}

                    {FixedContent}

                    {hasStructuredTextLink ? (
                        <StructuredTextRenderer
                            data={link}
                            itemLinkClass={styles.teaserCardLink}
                            itemLinkState={customLinkData}
                            noContainer
                            plainLink
                            emptyLink
                        />
                    ) : (
                        <Link
                            to={url}
                            linkClass={styles.teaserCardLink}
                            aria-label={ariaButtonLabel}
                            {...targetBlank}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
